/**
 * Strategy scorer — V105-5-4 / ADR-0072 §6 (D-105-3). PURE: no I/O, no clock, no randomness.
 *
 * Four strategy families (ADR-0075 §6 enum): hybrid, single_hyperscaler, inherit_boundary,
 * stay_commercial. stay_commercial is ALWAYS generated and scored, as the baseline.
 *
 * A strategy INSTANCE is a family plus one option per in-scope item (every item whose verdict
 * is not Out of scope). An option is one of
 *
 *   keep                           the item stays as it is (a documented third party)
 *   offering_move:<offering_key>   onto the provider's own authorized offering (reviewed
 *                                  provider-inheritance catalog coverage at the target level)
 *   substitute:<entry_id>          onto another service (an approved substitution-catalog row)
 *   partition:<pattern_id>         a reviewed partition pattern (`<entry_id>:<pattern>`)
 *   inherit_boundary:<entry_id>    run inside another company's authorized boundary
 *
 * `scoreStrategy(instance, items, ctx)` returns a VECTOR, never a weighted sum (ADR-0072 §6,
 * "a weighted strategy score" is a rejected alternative):
 *
 *   verdicts_resolved   Replace / Partition items the instance resolves, the unresolved ids and
 *                       the ids whose resolution is unknown (an abstained target, an unresolved
 *                       coverage state); pending verdicts are listed apart.
 *   inheritance_gained  objectives that move customer → provider | shared, from
 *                       `computeTransitionGap` run as a projection on a hypothetical target
 *                       built from the instance (nothing stored, no transition created).
 *   code_impact         call-site counts from the V105-3-1 repo scan for every item whose
 *                       option isn't `keep`, with file links. Counts, never effort (PRD §9).
 *   cost_band           the maximum catalog cost band over the chosen options, plus the count
 *                       per band; an abstained band is counted as unknown, never guessed.
 *   class_fit           pass | fail | unknown with reasons: every covering offering at least
 *                       the target level; no Replace / Partition item left unresolved; every
 *                       KSI reached has the FRC-CSX-VVK automated-method minimum its class needs
 *                       (the minimum is read from the approved rule set, never hard-coded).
 *
 * Every per-item line carries reason codes that cite its inputs (catalog entry id, coverage
 * state, scan run, verdict rule). Unknown is a first-class value: it is shown, never filled in.
 *
 * DISPLAY ORDER (`compareStrategyScores`) is ADR-0072 §6's lexicographic sort, for display only
 * (the owner chooses): class_fit pass first, then fewer unresolved, more inheritance_gained,
 * lower code_impact, lower cost_band. Unknowns never sort as better than a known value
 * (class_fit: pass < unknown < fail; fewer unknown resolutions next; an unknown cost band sorts
 * after any known band); ties fall back to the fixed family order below.
 *
 * GENERATION (`generateStrategyInstances`) is ADR-0072 §6's "each item's lowest-id option
 * compatible with the family", made deterministic where the ADR leaves a choice:
 *   - single_hyperscaler and inherit_boundary each pick ONE anchor (the target provider / the
 *     platform of the lowest-id option of that family over the in-scope items), so the
 *     instance names a single hyperscaler / boundary. A person swaps per element (V105-5-5).
 *   - A Replace / Partition item takes its lowest-id family option that resolves it (known
 *     first, then unknown); else a stay_commercial partition pattern that resolves it (keeping a
 *     service as a documented third party fits every family); else, in hybrid, the provider's
 *     own authorized offering; else keep.
 *   - Any other in-scope item takes its lowest-id family move option (hybrid / hyperscaler /
 *     boundary), else (hybrid, or the anchor hyperscaler's own items) the provider's authorized
 *     offering, else keep. stay_commercial never moves an item: every Replace stays unresolved
 *     and every Partition rests on its pattern only (ADR-0072 §6).
 */
import { canonicalJson, sha256Hex } from '../utils/audit-crypto.js';
import { HOSTING_FUNCTIONS } from './infra-verdict.js';
import { STRATEGY_FAMILIES, COST_BANDS } from './substitution-catalog-service.js';
import { resolveProductCoverage } from './product-coverage-service.js';
import { IMPACT_RANK, stripClassMarker } from './provider-inheritance-catalog-service.js';
import { catalogCandidates } from './transition-service.js';
import { computeTransitionGap } from './transition-gap.js';
import { productServiceNames } from './infra-verdict-inputs.js';
export const INFRA_STRATEGY_SCORER = 'infra-strategy-scorer@1';
export const INFRA_STRATEGY_FORMAT = 'gacpro.infra-strategy-score/1';
/**
 * Every reason code the scorer can put on an item line (the prefix before `:`), and the subset
 * that names a problem the person can act on or wait for (R2-H5: the UI words every code and
 * gives each problem an action or the "Waiting on GAC Pro review" tag; a test enforces it).
 */
export const STRATEGY_REASON_CODES = [
    'VERDICT', 'KEEP', 'KEEP_REPLACE_UNRESOLVED', 'KEEP_PARTITION_NOT_APPLIED', 'PARTITION_PATTERN', 'OFFERING_MOVE', 'COVERED_AT', 'NOT_COVERED_AT_LEVEL',
    'CATALOG', 'CATALOG_ROW_MISSING', 'CATALOG_UNVERIFIED', 'OPTION_NOT_IN_CATALOG', 'TARGET_OFFERING', 'TARGET_OFFERING_ABSTAINED', 'TARGET_AT_LEVEL',
    'TARGET_BELOW_LEVEL', 'TARGET_LEVEL_UNKNOWN', 'TARGET_COVERAGE', 'TARGET_PRODUCT_ABSTAINED', 'CODE_CHANGE_NONE', 'CALL_SITES', 'CALL_SITES_UNKNOWN',
    'COST_BAND', 'COST_BAND_ESTIMATE', 'COST_BAND_ABSTAINED', 'COST_BAND_NO_CATALOG_ROW', 'INHERITANCE', 'INHERITANCE_UNKNOWN', 'NOT_HOSTING_NO_INHERITANCE',
];
export const STRATEGY_PROBLEM_CODES = [
    'KEEP_REPLACE_UNRESOLVED', 'KEEP_PARTITION_NOT_APPLIED', 'NOT_COVERED_AT_LEVEL', 'CATALOG_ROW_MISSING', 'CATALOG_UNVERIFIED', 'OPTION_NOT_IN_CATALOG',
    'TARGET_OFFERING_ABSTAINED', 'TARGET_BELOW_LEVEL', 'TARGET_LEVEL_UNKNOWN', 'TARGET_PRODUCT_ABSTAINED', 'CALL_SITES_UNKNOWN', 'COST_BAND_ABSTAINED',
    'COST_BAND_NO_CATALOG_ROW', 'INHERITANCE_UNKNOWN',
];
/** class_fit reason codes (item-level ones carry item_id; VVK_RULES_MISSING / VVK_NO_AUTOMATED_EVIDENCE / VVK_BELOW_MINIMUM are workspace-level). */
export const CLASS_FIT_CODES = [
    'VERDICT_PENDING', 'RESOLUTION_UNKNOWN', 'REPLACE_UNRESOLVED', 'PARTITION_UNRESOLVED', 'TARGET_BELOW_LEVEL', 'TARGET_NOT_COVERED', 'VVK_TARGET_EVIDENCE_UNKNOWN',
    'VVK_RULES_MISSING', 'VVK_NO_AUTOMATED_EVIDENCE', 'VVK_BELOW_MINIMUM',
];
export const STRATEGY_OPTION_KINDS = ['keep', 'offering_move', 'substitute', 'partition', 'inherit_boundary'];
/** Providers whose own government region counts as a "single hyperscaler" target. */
const HYPERSCALERS = new Set(['aws', 'azure', 'gcp']);
const byStr = (a, b) => (a < b ? -1 : a > b ? 1 : 0);
const NEEDS = new Set(['replace', 'partition']);
export function optionKindOf(option) {
    if (option === 'keep')
        return 'keep';
    const k = option.slice(0, option.indexOf(':'));
    return STRATEGY_OPTION_KINDS.includes(k) && k !== 'keep' ? k : null;
}
const optionRef = (option) => option.slice(option.indexOf(':') + 1);
const entriesFor = (item, catalog) => catalog.filter((e) => e.from_provider_key === item.provider_key && (e.from_product_id === item.product || e.from_product_id === '*'));
/** Every option the reviewed content offers for one item, sorted by id within kind (keep last). */
export function optionsForItem(item, ctx) {
    const out = [];
    const eligible = new Set(item.eligible_partition_ids);
    for (const e of [...entriesFor(item, ctx.catalog)].sort((a, b) => byStr(a.entry_id, b.entry_id))) {
        for (const p of e.partition_patterns ?? []) {
            const id = `${e.entry_id}:${p.pattern}`;
            if (!eligible.has(id))
                continue;
            out.push({ option: `partition:${id}`, kind: 'partition', family: e.strategy_family, entry_id: e.entry_id, verified: e.verified,
                target: { provider_key: e.to_provider_key, product_id: e.to_product_id, offering: e.to_offering_id, platform: null, pattern: p.pattern } });
        }
        if (e.strategy_family === 'inherit_boundary') {
            out.push({ option: `inherit_boundary:${e.entry_id}`, kind: 'inherit_boundary', family: e.strategy_family, entry_id: e.entry_id, verified: e.verified,
                target: { provider_key: null, product_id: null, offering: e.to_offering_id, platform: e.boundary_platform, pattern: null } });
        }
        else if (e.strategy_family !== 'stay_commercial' && e.to_provider_key) {
            out.push({ option: `substitute:${e.entry_id}`, kind: 'substitute', family: e.strategy_family, entry_id: e.entry_id, verified: e.verified,
                target: { provider_key: e.to_provider_key, product_id: e.to_product_id, offering: e.to_offering_id, platform: null, pattern: null } });
        }
    }
    const cov = item.q3?.coverage;
    if (cov?.covered && !cov.current_offering_covered) {
        for (const k of [...cov.offering_keys].sort()) {
            out.push({ option: `offering_move:${k}`, kind: 'offering_move', family: HYPERSCALERS.has(item.provider_key) ? 'single_hyperscaler' : 'hybrid', entry_id: null, verified: null,
                target: { provider_key: item.provider_key, product_id: item.product, offering: k, platform: null, pattern: null } });
        }
    }
    out.push({ option: 'keep', kind: 'keep', family: null, entry_id: null, verified: null, target: { provider_key: null, product_id: null, offering: null, platform: null, pattern: null } });
    return out;
}
const levelOk = (impact, level) => impact ? ((IMPACT_RANK[impact.toLowerCase()] ?? 0) >= IMPACT_RANK[level] ? 'yes' : 'no') : 'unknown';
/** The approved inheritance offering(s) for a FedRAMP package id, at or above the level (lowest sufficient first). */
function inheritanceOffering(ctx, packageId, providerKey) {
    return ctx.inheritance.offerings
        .filter((o) => o.fedramp_package_id === packageId && (!providerKey || o.provider_key === providerKey) && levelOk(o.impact_level, ctx.target_impact_level) === 'yes')
        .sort((a, b) => (IMPACT_RANK[a.impact_level] ?? 0) - (IMPACT_RANK[b.impact_level] ?? 0) || byStr(a.offering_key, b.offering_key))[0] ?? null;
}
const COVERAGE_TRI = { covered: 'yes', not_covered: 'no', conflict: 'unknown', provider_asserted: 'unknown', unmapped: 'unknown' };
function evaluateOption(item, opt, entry, ctx) {
    const needs = NEEDS.has(item.verdict.verdict);
    const out = { resolves: needs ? 'no' : 'n/a', reasons: [], offering: null, inheritance_unknown: false, fit: [] };
    const level = ctx.target_impact_level;
    if (opt.kind === 'keep') {
        if (needs)
            out.reasons.push(item.verdict.verdict === 'replace' ? 'KEEP_REPLACE_UNRESOLVED' : 'KEEP_PARTITION_NOT_APPLIED');
        else
            out.reasons.push('KEEP');
        return out;
    }
    if (opt.kind === 'partition') {
        // Eligibility is the verdict engine's (one pattern at a time); the option only exists when eligible.
        out.resolves = item.verdict.verdict === 'partition' || item.verdict.verdict === 'replace' ? 'yes' : 'n/a';
        out.reasons.push(`PARTITION_PATTERN:${optionRef(opt.option)}`);
        return out;
    }
    if (opt.kind === 'offering_move') {
        const key = optionRef(opt.option);
        const o = ctx.inheritance.offerings.find((x) => x.offering_key === key) ?? null;
        const covered = !!item.q3?.coverage?.offering_keys?.includes(key);
        out.reasons.push(`OFFERING_MOVE:${key}`, covered ? `COVERED_AT:${level}` : 'NOT_COVERED_AT_LEVEL');
        if (needs)
            out.resolves = covered ? 'yes' : 'no';
        if (!covered)
            out.fit.push({ code: 'TARGET_NOT_COVERED', detail: `${item.ref} is not covered by ${key} at ${level}` });
        if (o)
            out.offering = { offering_key: o.offering_key, services: productServiceNames(ctx.coverage, item.provider_key, item.product) };
        else
            out.inheritance_unknown = true;
        return out;
    }
    // substitute / inherit_boundary: a catalog row
    if (!entry) {
        out.resolves = needs ? 'unknown' : 'n/a';
        out.reasons.push('CATALOG_ROW_MISSING');
        return out;
    }
    out.reasons.push(`CATALOG:${entry.entry_id}`);
    if (!entry.verified)
        out.reasons.push('CATALOG_UNVERIFIED');
    const offeringId = entry.to_offering_id;
    if (!offeringId) {
        out.reasons.push('TARGET_OFFERING_ABSTAINED');
        out.inheritance_unknown = true;
        if (needs)
            out.resolves = 'unknown';
        return out;
    }
    const impact = ctx.coverage.offering_impact?.[offeringId] ?? null;
    const atLevel = levelOk(impact, level);
    out.reasons.push(`TARGET_OFFERING:${offeringId}`, atLevel === 'yes' ? `TARGET_AT_LEVEL:${level}` : atLevel === 'no' ? `TARGET_BELOW_LEVEL:${impact}` : 'TARGET_LEVEL_UNKNOWN');
    if (atLevel === 'no')
        out.fit.push({ code: 'TARGET_BELOW_LEVEL', detail: `${offeringId} is authorized at ${impact}, below ${level}` });
    let covered = atLevel;
    if (opt.kind === 'substitute' && entry.to_provider_key && entry.to_product_id && atLevel !== 'no') {
        const r = resolveProductCoverage({ provider_key: entry.to_provider_key, product_id: entry.to_product_id }, { offering_id: offeringId }, ctx.coverage);
        out.reasons.push(`TARGET_COVERAGE:${r.state}`);
        covered = atLevel === 'unknown' ? (COVERAGE_TRI[r.state] === 'no' ? 'no' : 'unknown') : COVERAGE_TRI[r.state];
        if (r.state === 'not_covered')
            out.fit.push({ code: 'TARGET_NOT_COVERED', detail: `${entry.to_provider_key}/${entry.to_product_id} is not covered by ${offeringId}` });
    }
    else if (opt.kind === 'substitute' && !entry.to_product_id) {
        out.reasons.push('TARGET_PRODUCT_ABSTAINED');
        if (covered === 'yes')
            covered = 'unknown';
    }
    if (needs)
        out.resolves = covered;
    const o = inheritanceOffering(ctx, offeringId, opt.kind === 'substitute' ? entry.to_provider_key : null);
    if (o) {
        out.offering = { offering_key: o.offering_key, services: entry.to_provider_key && entry.to_product_id ? productServiceNames(ctx.coverage, entry.to_provider_key, entry.to_product_id) : [] };
    }
    else
        out.inheritance_unknown = true;
    return out;
}
export function describeStrategyChoice(item, option, ctx) {
    const opt = optionsForItem(item, ctx).find((o) => o.option === option) ?? null;
    const entry = opt?.entry_id ? ctx.catalog.find((e) => e.entry_id === opt.entry_id) ?? null : null;
    const ev = opt ? evaluateOption(item, opt, entry, ctx) : { resolves: 'unknown', reasons: ['OPTION_NOT_IN_CATALOG'], offering: null, inheritance_unknown: true, fit: [] };
    return { item_id: item.item_id, option, kind: optionKindOf(option), resolved: opt, entry, resolves: ev.resolves, reasons: ev.reasons, offering: ev.offering, inheritance_unknown: ev.inheritance_unknown };
}
// ---- generation ------------------------------------------------------------------------------------------------
const inScope = (items) => [...items].filter((i) => i.verdict.verdict !== 'out_of_scope').sort((a, b) => byStr(a.ref, b.ref) || byStr(a.item_id, b.item_id));
/** Every target a single-target family can anchor on (sorted): the hyperscalers / platforms the catalog names. */
export function strategyTargets(family, items, ctx) {
    if (family !== 'single_hyperscaler' && family !== 'inherit_boundary')
        return [];
    const scope = inScope(items);
    const set = new Set();
    for (const e of scope.flatMap((i) => entriesFor(i, ctx.catalog))) {
        if (e.strategy_family !== family)
            continue;
        const t = family === 'single_hyperscaler' ? e.to_provider_key : e.boundary_platform;
        if (t)
            set.add(t);
    }
    return [...set].sort();
}
function anchorFor(family, items, ctx) {
    if (family !== 'single_hyperscaler' && family !== 'inherit_boundary')
        return null;
    const rows = items.flatMap((i) => entriesFor(i, ctx.catalog)).filter((e) => e.strategy_family === family)
        .filter((e) => (family === 'single_hyperscaler' ? !!e.to_provider_key : !!e.boundary_platform))
        .sort((a, b) => byStr(a.entry_id, b.entry_id));
    const first = rows[0];
    return first ? (family === 'single_hyperscaler' ? first.to_provider_key : first.boundary_platform) : null;
}
function compatible(opt, family, anchor, item) {
    if (opt.kind === 'keep')
        return true;
    if (opt.kind === 'offering_move') {
        if (family === 'hybrid')
            return !HYPERSCALERS.has(item.provider_key);
        return family === 'single_hyperscaler' && item.provider_key === anchor;
    }
    if (opt.family !== family)
        return false;
    if (family === 'single_hyperscaler')
        return opt.target.provider_key === anchor;
    if (family === 'inherit_boundary')
        return opt.target.platform === anchor;
    return true;
}
function pickOption(item, family, anchor, ctx) {
    const opts = optionsForItem(item, ctx);
    const entry = (o) => (o.entry_id ? ctx.catalog.find((e) => e.entry_id === o.entry_id) ?? null : null);
    const resolution = (o) => evaluateOption(item, o, entry(o), ctx).resolves;
    const fam = opts.filter((o) => o.kind !== 'keep' && o.kind !== 'offering_move' && compatible(o, family, anchor, item));
    const moves = opts.filter((o) => o.kind === 'offering_move' && compatible(o, family, anchor, item));
    if (NEEDS.has(item.verdict.verdict)) {
        if (family === 'stay_commercial') {
            return fam.find((o) => o.kind === 'partition' && resolution(o) === 'yes')?.option ?? 'keep';
        }
        const known = fam.find((o) => resolution(o) === 'yes') ?? fam.find((o) => resolution(o) === 'unknown');
        if (known)
            return known.option;
        const kept = opts.find((o) => o.kind === 'partition' && o.family === 'stay_commercial' && resolution(o) === 'yes');
        if (kept)
            return kept.option;
        const move = moves.find((o) => resolution(o) === 'yes');
        return move?.option ?? 'keep';
    }
    if (family === 'stay_commercial')
        return 'keep';
    const mover = fam.find((o) => o.kind === 'substitute' || o.kind === 'inherit_boundary');
    if (mover)
        return mover.option;
    return moves[0]?.option ?? 'keep';
}
/** One generated instance for a family and anchor (null anchor = the family's single instance). */
export function generateStrategyInstance(items, ctx, family, anchor) {
    const scope = inScope(items);
    return { family, anchor, generated: true, choices: scope.map((i) => ({ item_id: i.item_id, option: pickOption(i, family, anchor, ctx) })) };
}
/** One generated instance per family (stay_commercial always), each single-target family on its lowest-id target. */
export function generateStrategyInstances(items, ctx) {
    const scope = inScope(items);
    return STRATEGY_FAMILIES.map((family) => generateStrategyInstance(items, ctx, family, anchorFor(family, scope, ctx)));
}
/**
 * Owner decision D1 (2026-10-08): ONE candidate per target for the single-target families
 * (one government cloud: every hyperscaler the catalog names; inherit a boundary: every platform),
 * one instance for hybrid and stay_commercial. A family with no target keeps one instance.
 */
export function generateStrategyCandidates(items, ctx) {
    return STRATEGY_FAMILIES.flatMap((family) => {
        const targets = strategyTargets(family, items, ctx);
        return targets.length ? targets.map((t) => generateStrategyInstance(items, ctx, family, t)) : [generateStrategyInstance(items, ctx, family, null)];
    });
}
/** Validate a person's swaps against the options the content offers (unknown item / option → error text). */
export function applyStrategySwaps(base, swaps, items, ctx) {
    const byId = new Map(items.map((i) => [i.item_id, i]));
    const choices = new Map(base.choices.map((c) => [c.item_id, c.option]));
    for (const s of swaps) {
        const item = byId.get(s.item_id);
        if (!item || !choices.has(s.item_id))
            return { error: 'That service is not part of this strategy.', item_id: s.item_id };
        if (!optionsForItem(item, ctx).some((o) => o.option === s.option))
            return { error: 'That option is not in the reviewed catalog for this service.', item_id: s.item_id };
        choices.set(s.item_id, s.option);
    }
    const next = base.choices.map((c) => ({ item_id: c.item_id, option: choices.get(c.item_id) }));
    const changed = next.some((c, i) => c.option !== base.choices[i].option);
    return { family: base.family, anchor: base.anchor, generated: base.generated && !changed, choices: next };
}
const FILES_CAP = 200;
const BAND_RANK = Object.fromEntries(COST_BANDS.map((b, i) => [b, i]));
/** The vector for one instance (ADR-0072 §6). Async only for the gap hash; pure otherwise. */
export async function scoreStrategy(instance, items, ctx) {
    const byId = new Map(items.map((i) => [i.item_id, i]));
    const level = ctx.target_impact_level;
    const vr = {
        replace: { total: 0, resolved: 0, unresolved_item_ids: [], unknown_item_ids: [] },
        partition: { total: 0, resolved: 0, unresolved_item_ids: [], unknown_item_ids: [] },
        unresolved_item_ids: [], unknown_item_ids: [], pending_item_ids: [],
    };
    const code = { call_sites: 0, files: [], unknown_item_ids: [] };
    const counts = Object.fromEntries([...COST_BANDS, 'unknown'].map((b) => [b, 0]));
    let maxBand = null;
    let estimated = 0;
    const fit = [];
    const fitUnknown = [];
    const scored = [];
    // Projection inputs (ADR-0072 §6: computeTransitionGap on a hypothetical target, nothing stored).
    const current = [];
    const target = [];
    const proposals = [];
    const proposed = new Set();
    const gainedBy = new Map(); // objective → item
    const inheritanceUnknown = [];
    const kept = []; // items whose evidence (Q4) stays as recorded
    const choices = [...instance.choices].sort((a, b) => byStr(byId.get(a.item_id)?.ref ?? a.item_id, byId.get(b.item_id)?.ref ?? b.item_id));
    for (const ch of choices) {
        const item = byId.get(ch.item_id);
        if (!item)
            continue;
        const kind = optionKindOf(ch.option);
        const opt = optionsForItem(item, ctx).find((o) => o.option === ch.option) ?? null;
        const entry = opt?.entry_id ? ctx.catalog.find((e) => e.entry_id === opt.entry_id) ?? null : null;
        const ev = opt ? evaluateOption(item, opt, entry, ctx) : { resolves: 'unknown', reasons: ['OPTION_NOT_IN_CATALOG'], offering: null, inheritance_unknown: true, fit: [] };
        const reasons = [`VERDICT:${item.verdict.verdict}:${item.verdict.rule}`, ...ev.reasons];
        const v = item.verdict.verdict;
        // verdicts_resolved
        if (v === 'pending') {
            vr.pending_item_ids.push(item.item_id);
            fitUnknown.push({ code: 'VERDICT_PENDING', item_id: item.item_id, detail: `${item.name} still needs answers` });
        }
        else if (v === 'replace' || v === 'partition') {
            const bucket = vr[v];
            bucket.total++;
            if (ev.resolves === 'yes')
                bucket.resolved++;
            else if (ev.resolves === 'unknown') {
                bucket.unknown_item_ids.push(item.item_id);
                vr.unknown_item_ids.push(item.item_id);
                fitUnknown.push({ code: 'RESOLUTION_UNKNOWN', item_id: item.item_id, detail: `${item.name}: whether the option resolves it is not known` });
            }
            else {
                bucket.unresolved_item_ids.push(item.item_id);
                vr.unresolved_item_ids.push(item.item_id);
                fit.push({ code: v === 'replace' ? 'REPLACE_UNRESOLVED' : 'PARTITION_UNRESOLVED', item_id: item.item_id, detail: `${item.name} is left ${v === 'replace' ? 'to replace' : 'without its partition'}` });
            }
        }
        for (const f of ev.fit)
            fit.push({ ...f, item_id: item.item_id });
        // code_impact: every non-keep option; a catalog row whose code change is `none` counts 0.
        let sites = 0;
        if (kind && kind !== 'keep') {
            if (entry?.code_change_pattern === 'none') {
                reasons.push(`CODE_CHANGE_NONE:${entry.entry_id}`);
            }
            else if (!item.call_sites) {
                sites = null;
                code.unknown_item_ids.push(item.item_id);
                reasons.push('CALL_SITES_UNKNOWN');
            }
            else {
                sites = item.call_sites.count;
                code.call_sites += sites;
                reasons.push(`CALL_SITES:${sites}${item.call_sites.run_id ? `@${item.call_sites.run_id}` : ''}`);
                for (const f of item.call_sites.files)
                    if (code.files.length < FILES_CAP)
                        code.files.push({ item_id: item.item_id, path: f.path, line: f.line });
            }
        }
        // cost_band: the maximum over chosen (non-keep) options; abstained = unknown.
        let band = null;
        let costSource = null;
        if (kind && kind !== 'keep') {
            const est = item.cost_estimates?.[ch.option];
            if (entry?.cost_band) {
                band = entry.cost_band;
                costSource = 'catalog';
                reasons.push(`COST_BAND:${band}`);
            }
            else if (est) {
                band = est.band;
                costSource = 'estimate';
                estimated++;
                reasons.push(`COST_BAND_ESTIMATE:${band}`);
            }
            else {
                band = 'unknown';
                reasons.push(entry ? `COST_BAND_ABSTAINED:${entry.entry_id}` : 'COST_BAND_NO_CATALOG_ROW');
            }
            counts[band]++;
            if (band !== 'unknown' && (maxBand === null || BAND_RANK[band] > BAND_RANK[maxBand]))
                maxBand = band;
        }
        // inheritance projection: one current component per item; a moved HOSTING item gets a replacement.
        const cur = {
            component_id: `cur:${item.item_id}`, component_key: item.ref, system_id: null, external_service_id: `svc:${item.ref}`, inherited_from_service_id: null,
            federal_data: 'handles', security_category: level,
        };
        current.push(cur);
        const moves = kind === 'substitute' || kind === 'offering_move' || kind === 'inherit_boundary';
        let gained = 0;
        if (moves && item.function && HOSTING_FUNCTIONS.has(item.function)) {
            if (!ev.offering || ev.inheritance_unknown) {
                gained = null;
                inheritanceUnknown.push(item.item_id);
                reasons.push('INHERITANCE_UNKNOWN');
                target.push(cur);
            }
            else {
                const svc = `offering:${ev.offering.offering_key}`;
                target.push({ ...cur, component_id: `tgt:${item.item_id}`, replaces_component_id: cur.component_id, external_service_id: svc, inherited_from_service_id: svc, provider_service: ev.offering.services[0] ?? null });
                const o = ctx.inheritance.offerings.find((x) => x.offering_key === ev.offering.offering_key);
                const rows = o.rows.map((r) => ({ ...r, service_scope: r.service_scope.map((x) => (x === '*' ? x : stripClassMarker(x))) }));
                const cand = catalogCandidates(rows, { services: new Set(ev.offering.services), requirementSet: ctx.inheritance.requirementSet, byRequirement: ctx.inheritance.byRequirement, externalServiceId: svc });
                let n = 0;
                for (const [objective, c] of [...cand].sort((a, b) => byStr(a[0], b[0]))) {
                    if (c.party !== 'provider' && c.party !== 'shared')
                        continue;
                    if (proposed.has(objective))
                        continue;
                    proposed.add(objective);
                    gainedBy.set(objective, item.item_id);
                    n++;
                    proposals.push({
                        proposal_id: `proj:${objective}`, objective_id: objective, party: c.party, external_service_id: svc, attestation_document_id: null,
                        customer_part: c.customer_part, status: 'confirmed', basis: 'catalog', catalog_row_id: c.catalog_row_id, catalog_verified: c.catalog_verified, labels: c.labels,
                    });
                }
                reasons.push(`INHERITANCE:${ev.offering.offering_key}`);
                gained = n;
            }
        }
        else {
            if (moves)
                reasons.push('NOT_HOSTING_NO_INHERITANCE');
            target.push(cur);
        }
        if (!moves && v !== 'pending')
            kept.push(item);
        if (moves)
            fitUnknown.push(...(ctx.vvk.applicable ? [{ code: 'VVK_TARGET_EVIDENCE_UNKNOWN', item_id: item.item_id, detail: `${item.name}: automated evidence after the move is not known yet` }] : []));
        scored.push({ item_id: item.item_id, ref: item.ref, name: item.name, verdict: v, option: ch.option, kind, resolves: ev.resolves, call_sites: sites, cost_band: band, cost_source: costSource, inheritance_gained: gained, reasons });
    }
    const gap = await computeTransitionGap({
        transition_id: `projection:${instance.family}`, profile_id: 'projection', program: 'projection', target_service_id: 'projection', offering_key: null,
        program_impact: null, target_impact: null, offering_services: null,
        current: { components: current, interconnection_service_ids: [] }, target: { components: target, interconnection_service_ids: [] },
        services: [], current_responsibility: [...(ctx.current_responsibility ?? [])], proposals, objectives: [], current_status: {}, attestation_covered: {},
    });
    const gainedIds = gap.objectives.filter((o) => o.change === 'gained').map((o) => o.objective_id).sort();
    // Per item: only objectives that are gained (not already provider / shared today).
    const gainedSet = new Set(gainedIds);
    for (const s of scored) {
        if (s.inheritance_gained === null || s.inheritance_gained === 0)
            continue;
        s.inheritance_gained = [...gainedBy].filter(([o, it]) => it === s.item_id && gainedSet.has(o)).length;
    }
    // class_fit, VVK (FRC-CSX-VVK minimum per KSI over the evidence that stays as recorded).
    if (ctx.vvk.applicable) {
        if (!ctx.vvk.minimum)
            fitUnknown.push({ code: 'VVK_RULES_MISSING', detail: `No approved automated-method minimum for class ${ctx.vvk.certification_class ?? 'unknown'}` });
        else if (ctx.vvk.minimum.minimum > 0) {
            const methods = new Map();
            for (const it of kept) {
                const q4 = it.q4;
                if (!q4)
                    continue;
                for (const k of q4.ksi_objective_ids ?? []) {
                    const set = methods.get(k) ?? new Set();
                    for (const ck of q4.checks ?? [])
                        set.add(ck);
                    methods.set(k, set);
                }
            }
            if (!methods.size && kept.length)
                fit.push({ code: 'VVK_NO_AUTOMATED_EVIDENCE', detail: `No KSI has automated evidence; class ${ctx.vvk.certification_class} needs ${ctx.vvk.minimum.minimum} (${ctx.vvk.minimum.force})` });
            for (const [k, set] of [...methods].sort((a, b) => byStr(a[0], b[0]))) {
                if (set.size < ctx.vvk.minimum.minimum)
                    fit.push({ code: 'VVK_BELOW_MINIMUM', detail: `${k}: ${set.size} of ${ctx.vvk.minimum.minimum} automated methods (FRC-CSX-VVK ${ctx.vvk.minimum.force})` });
            }
        }
    }
    const status = fit.length ? 'fail' : fitUnknown.length ? 'unknown' : 'pass';
    return {
        format: INFRA_STRATEGY_FORMAT, scorer: INFRA_STRATEGY_SCORER, family: instance.family, anchor: instance.anchor, generated: instance.generated, target_impact_level: level,
        verdicts_resolved: vr,
        inheritance_gained: { count: gainedIds.length, objective_ids: gainedIds, gap_sha256: gap.gap_sha256, unknown_item_ids: inheritanceUnknown },
        code_impact: code,
        cost_band: { max: maxBand, counts, estimated },
        class_fit: { status, reasons: [...fit, ...fitUnknown] },
        items: scored,
    };
}
// ---- display order (ADR-0072 §6: a sort for display only; the owner chooses) ------------------------------------
const FIT_RANK = { pass: 0, unknown: 1, fail: 2 };
const familyRank = (f) => STRATEGY_FAMILIES.indexOf(f);
const bandKey = (v) => (v.cost_band.max ? BAND_RANK[v.cost_band.max] : v.cost_band.counts.unknown ? COST_BANDS.length : -1);
/** Lexicographic: class_fit, fewer unresolved (then fewer unknown), more inheritance, fewer call sites, lower band, family order. */
export function compareStrategyScores(a, b) {
    return FIT_RANK[a.class_fit.status] - FIT_RANK[b.class_fit.status]
        || a.verdicts_resolved.unresolved_item_ids.length - b.verdicts_resolved.unresolved_item_ids.length
        || a.verdicts_resolved.unknown_item_ids.length - b.verdicts_resolved.unknown_item_ids.length
        || b.inheritance_gained.count - a.inheritance_gained.count
        || a.code_impact.call_sites - b.code_impact.call_sites
        || a.code_impact.unknown_item_ids.length - b.code_impact.unknown_item_ids.length
        || bandKey(a) - bandKey(b)
        || familyRank(a.family) - familyRank(b.family);
}
// ---- hashing ---------------------------------------------------------------------------------------------------
/** SHA-256 over exactly what the scorer reads (items' verdict facts + options content + level + VVK). */
export async function strategyInputsSha256(items, ctx) {
    const body = {
        scorer: INFRA_STRATEGY_SCORER,
        target_impact_level: ctx.target_impact_level,
        items: inScope(items).map((i) => ({
            item_id: i.item_id, ref: i.ref, function: i.function, verdict: i.verdict.verdict, rule: i.verdict.rule, inputs_sha256: i.verdict.inputs_sha256 ?? null,
            q3_offering_keys: i.q3?.coverage?.offering_keys ?? [], current_offering_covered: !!i.q3?.coverage?.current_offering_covered,
            q4: i.q4 ? { status: i.q4.status, ksi: [...(i.q4.ksi_objective_ids ?? [])].sort(), checks: [...(i.q4.checks ?? [])].sort() } : null,
            eligible_partition_ids: [...i.eligible_partition_ids].sort(), call_sites: i.call_sites ? { count: i.call_sites.count, run_id: i.call_sites.run_id } : null,
            cost_estimates: Object.fromEntries(Object.entries(i.cost_estimates ?? {}).map(([o, e]) => [o, { band: e.band, by: e.estimated_by, at: e.estimated_at }]).sort((x, y) => byStr(x[0], y[0]))),
        })),
        catalog: [...ctx.catalog].sort((a, b) => byStr(a.entry_id, b.entry_id)),
        inheritance: ctx.inheritance.offerings.map((o) => ({ key: o.offering_key, pkg: o.fedramp_package_id, level: o.impact_level, rows: o.rows.map((r) => r.row_id).sort() })).sort((a, b) => byStr(a.key, b.key)),
        coverage: { map: ctx.coverage.map.map((m) => m.map_id).sort(), claims: ctx.coverage.claims.length, impact: ctx.coverage.offering_impact ?? {} },
        current_responsibility: [...(ctx.current_responsibility ?? [])].map((r) => `${r.objective_id}:${r.party}`).sort(),
        vvk: ctx.vvk,
    };
    return sha256Hex(canonicalJson(body));
}
/** SHA-256 of one scored instance (instance + vector), as stored with a score run. */
export async function strategyScoreSha256(instance, vector) {
    return sha256Hex(canonicalJson({ instance, vector }));
}
/** Score every instance and return them in display order. */
export async function scoreStrategies(instances, items, ctx) {
    const out = [];
    for (const instance of instances)
        out.push({ instance, vector: await scoreStrategy(instance, items, ctx) });
    return out.sort((a, b) => compareStrategyScores(a.vector, b.vector));
}
const candidateKey = (i) => `${i.family}:${i.anchor ?? ''}`;
export const strategyCandidateKey = candidateKey;
/** Why `a` sorts before `b`: the first display-order key that separates them (or TIE). */
export function leadReason(a, b) {
    if (!b)
        return 'ONLY_CANDIDATE';
    if (FIT_RANK[a.class_fit.status] !== FIT_RANK[b.class_fit.status])
        return `FITS:${a.class_fit.status}`;
    if (a.verdicts_resolved.unresolved_item_ids.length !== b.verdicts_resolved.unresolved_item_ids.length)
        return `FEWER_OPEN:${a.verdicts_resolved.unresolved_item_ids.length}`;
    if (a.verdicts_resolved.unknown_item_ids.length !== b.verdicts_resolved.unknown_item_ids.length)
        return `FEWER_UNKNOWN:${a.verdicts_resolved.unknown_item_ids.length}`;
    if (a.inheritance_gained.count !== b.inheritance_gained.count)
        return `MORE_INHERITANCE:${a.inheritance_gained.count}`;
    if (a.code_impact.call_sites !== b.code_impact.call_sites)
        return `LESS_CODE:${a.code_impact.call_sites}`;
    if (a.code_impact.unknown_item_ids.length !== b.code_impact.unknown_item_ids.length)
        return `FEWER_UNSCANNED:${a.code_impact.unknown_item_ids.length}`;
    if (bandKey(a) !== bandKey(b))
        return `LOWER_COST:${a.cost_band.max ?? 'unknown'}`;
    return 'TIE_FAMILY_ORDER';
}
/** Group scored candidates per family (fixed family order) with the recommended one first. */
export function groupStrategyCandidates(scored) {
    return STRATEGY_FAMILIES.map((family) => {
        const candidates = scored.filter((s) => s.instance.family === family).slice().sort((a, b) => compareStrategyScores(a.vector, b.vector) || byStr(a.instance.anchor ?? '', b.instance.anchor ?? ''));
        const first = candidates[0];
        return {
            family, candidates,
            recommended: first ? candidateKey(first.instance) : null,
            recommended_reasons: first ? [leadReason(first.vector, candidates[1]?.vector ?? null)] : [],
        };
    });
}
/** The overall leader across families (first recommended candidate in display order) and why. */
export function overallLeader(groups) {
    const tops = groups.map((g) => g.candidates[0]).filter((x) => !!x).sort((a, b) => compareStrategyScores(a.vector, b.vector));
    const t = tops[0];
    return t ? { key: candidateKey(t.instance), family: t.instance.family, status: t.vector.class_fit.status, reason: leadReason(t.vector, tops[1]?.vector ?? null) } : null;
}
export const STRATEGY_FAMILY_LABEL = Object.freeze({
    hybrid: 'Hybrid', single_hyperscaler: 'One government cloud', inherit_boundary: 'Inherit a boundary', stay_commercial: 'Stay commercial',
});
//# sourceMappingURL=infra-strategy.js.map